import { useCallback } from 'react';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { Draft } from 'immer';
import { getOriginalBlockId, isRuntimeBlockId } from '../../utils/runTimeBlocks';
import { useGlobalStateStore } from '../../stores/GlobalStateStore';
import { useGetAndSetRunTimeBlockState } from '../useGetAndSetRunTimeBlockState';
import { useDependencyGraphProvider } from '../../context/DependencyGraphContext';
import { useInterfaceStore } from '../../stores/InterfaceStore';
import { ComputeContextKeys } from '../computeContext/const';
import { useGetBlockStateId } from '../useGetBlockStateId';

function useSetBlockState({
  originalBlockId,
  runtimeBlockKey,
}: {
  originalBlockId: string;
  runtimeBlockKey?: string;
}) {
  const { blockStateId } = useGetBlockStateId({
    originalBlockId,
    runtimeBlockKey,
  });
  const activeInterfacePageId = useInterfaceStore().use.activeInterfacePageId();
  const interfaceId = useInterfaceStore().use.record.id();
  const {
    actions: { notifyDependencyChange },
  } = useDependencyGraphProvider();
  const { updateBlockState: updateBlockStateStore, setInterfaceSessionStorage } =
    useGlobalStateStore().use.actions();

  // in the case of !runTimeBlockKey, originalBlockState is the same as runTimeBlockState
  const originalBlockState = useGlobalStateStore().use.block.details(originalBlockId);

  const runTimeBlockState = useGetAndSetRunTimeBlockState({
    originalBlockState,
    blockStateId,
    runtimeBlockKey,
  });

  const updateBlockState = useCallback(
    (setter: (blockState: Draft<BlockStateUnionType>) => void) => {
      updateBlockStateStore(blockStateId, setter);
      notifyDependencyChange({
        id: isRuntimeBlockId(blockStateId) ? getOriginalBlockId(blockStateId) : blockStateId,
      });
    },
    [blockStateId, notifyDependencyChange, updateBlockStateStore],
  );

  const updateBlockSessionState = useCallback(
    (state: unknown) => {
      setInterfaceSessionStorage({
        operation: 'SET_IN_BLOCK_STATE',
        payload: {
          path: [originalBlockId],
          value: state,
          interfaceId,
          interfacePageId: activeInterfacePageId,
        },
      });

      notifyDependencyChange({
        id: ComputeContextKeys.SESSION_STORAGE,
      });
    },
    [
      activeInterfacePageId,
      interfaceId,
      notifyDependencyChange,
      originalBlockId,
      setInterfaceSessionStorage,
    ],
  );

  return {
    blockStateId,
    blockState: runtimeBlockKey ? runTimeBlockState : originalBlockState,
    updateBlockState,
    updateBlockSessionState,
  };
}

export default useSetBlockState;

export type UseSetBlockStateReturnType = ReturnType<typeof useSetBlockState>;
